import {
  AlertTriangle,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Target,
  Wand2,
  Sparkles,
  Pause,
  PlusCircle,
  Activity,
} from "lucide-react";
import { glass, BURNT, BURNT_GLOW, GREEN, ORANGE, RED } from "./shared";

type Severity = "critical" | "high" | "medium" | "low";
type Workflow = "ad" | "keywords" | "budget" | "intent" | "negate" | "tracking";
type Insight = {
  id: string;
  title: string;
  severity: Severity;
  impact: string;
  explanation: string;
  action: string;
  actionLabel: string;
  icon: any;
  prompt: string;
  campaign: string;
  adGroup?: string;
  workflow: Workflow;
};

const INSIGHTS: Insight[] = [
  {
    id: "1",
    title: "High CPC on Performance Max",
    severity: "high",
    impact: "−$420/wk",
    explanation:
      "Your *Performance Max — Spring* campaign is paying $1.06 CPC, ~52% above account average. Bidding strategy is over-spending on low-intent placements.",
    action: "optimize",
    actionLabel: "Optimize Now",
    icon: TrendingUp,
    prompt: "Optimize Performance Max — Spring to reduce CPC",
    campaign: "Performance Max — Spring",
    workflow: "budget",
  },
  {
    id: "2",
    title: "Low CTR on Brand Awareness ad group",
    severity: "medium",
    impact: "+18% CTR",
    explanation:
      "*Brand Awareness Q2 › Cold Audience* CTR is 1.4%, well below the 3.4% account avg. Two creatives are 6 weeks old and showing fatigue.",
    action: "ads",
    actionLabel: "Generate New Ads",
    icon: Wand2,
    prompt: "Generate fresh ads for Brand Awareness Q2 Cold Audience",
    campaign: "Brand Awareness Q2",
    adGroup: "Cold Audience",
    workflow: "ad",
  },
  {
    id: "3",
    title: "Lead Gen Search is budget-capped",
    severity: "high",
    impact: "+92 conv/mo",
    explanation:
      "Lead Gen Search is hitting daily budget by 11am most days. Impression share lost to budget is **34%** — you're leaving conversions on the table.",
    action: "budget",
    actionLabel: "Increase Budget",
    icon: DollarSign,
    prompt: "Increase budget on Lead Gen Search to lift impression share",
    campaign: "Lead Gen Search",
    workflow: "budget",
  },
  {
    id: "4",
    title: "Wasted spend on broad-match terms",
    severity: "critical",
    impact: "−$612 wasted",
    explanation:
      "12 search terms have spent >$40 each with **zero conversions** in 30 days. Mostly broad-match leakage on informational queries.",
    action: "negate",
    actionLabel: "Pause Wasteful Keywords",
    icon: Pause,
    prompt: "Pause wasted broad-match search terms with zero conversions",
    campaign: "Lead Gen Search",
    workflow: "negate",
  },
  {
    id: "5",
    title: "Missing conversion tracking",
    severity: "medium",
    impact: "Unknown ROAS",
    explanation:
      "*Local Service Ads* has no purchase event configured. ROAS is currently estimated from clicks — true performance is uncertain.",
    action: "tracking",
    actionLabel: "Optimize Now",
    icon: Activity,
    prompt: "Help me set up conversion tracking on Local Service Ads",
    campaign: "Local Service Ads",
    workflow: "tracking",
  },
  {
    id: "6",
    title: "Scaling opportunity on top keyword",
    severity: "low",
    impact: "+$2.4K rev",
    explanation:
      "Keyword *[google ads optimization]* is converting at 8.4% with strong margin. Increasing the bid 18% would likely 2x conversions on this term.",
    action: "scale",
    actionLabel: "Optimize Now",
    icon: TrendingUp,
    prompt: "Scale the [google ads optimization] keyword by raising bids",
    campaign: "Lead Gen Search",
    adGroup: "High Intent",
    workflow: "keywords",
  },
];

const SEV: Record<Severity, { label: string; color: string; bg: string }> = {
  critical: { label: "Critical", color: RED, bg: `${RED}22` },
  high: { label: "High", color: BURNT_GLOW, bg: `${BURNT}22` },
  medium: { label: "Medium", color: ORANGE, bg: `${ORANGE}22` },
  low: { label: "Low", color: GREEN, bg: `${GREEN}22` },
};

const ACTION_ICONS: Record<string, any> = {
  optimize: Wand2,
  ads: Sparkles,
  budget: PlusCircle,
  negate: Pause,
  tracking: Target,
  scale: TrendingUp,
};

const InsightsTab = ({
  onTriggerAria,
}: {
  onTriggerAria: (prompt: string, ctx?: Record<string, any>) => void;
}) => {
  return (
    <div className="space-y-5">
      {/* Top status bar */}
      <div
        className="flex flex-wrap items-center justify-between gap-3 rounded-2xl px-5 py-4"
        style={glass}
      >
        <div className="flex items-center gap-3">
          <div
            className="relative flex h-10 w-10 items-center justify-center rounded-xl"
            style={{
              background: `${BURNT}1f`,
              border: `1px solid ${BURNT}55`,
              boxShadow: `0 0 22px ${BURNT}55`,
            }}
          >
            <Sparkles size={16} style={{ color: BURNT_GLOW }} />
            <span
              className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 rounded-full"
              style={{ background: GREEN, boxShadow: `0 0 10px ${GREEN}` }}
            />
          </div>
          <div>
            <div className="flex items-center gap-2 text-[14px] font-bold text-white">
              ARIA Active
              <span className="rounded-full px-2 py-0.5 text-[10px] font-bold" style={{ background: `${GREEN}22`, color: GREEN }}>
                Analyzing
              </span>
            </div>
            <div className="text-[11.5px] text-white/55">
              Last analysis · 4 min ago · {INSIGHTS.length} recommendations found
            </div>
          </div>
        </div>
        <div className="flex items-center gap-4 text-[12px]">
          <div>
            <div className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-white/45">Confidence</div>
            <div className="mt-1 flex items-center gap-2">
              <div className="h-1.5 w-32 overflow-hidden rounded-full bg-white/8">
                <div
                  className="h-full rounded-full"
                  style={{
                    width: "92%",
                    background: `linear-gradient(90deg, ${BURNT}, ${BURNT_GLOW})`,
                  }}
                />
              </div>
              <span className="font-bold text-white">92%</span>
            </div>
          </div>
        </div>
      </div>

      {/* Grid */}
      <div data-tour-id="insights-grid" className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {INSIGHTS.map((ins) => {
          const sev = SEV[ins.severity];
          const Icon = ins.icon;
          const ActionIcon = ACTION_ICONS[ins.action] || Wand2;
          const positive = ins.impact.startsWith("+");
          return (
            <div
              key={ins.id}
              className="group relative flex flex-col rounded-2xl p-5 transition-all hover:-translate-y-0.5"
              style={glass}
            >
              <div
                aria-hidden
                className="pointer-events-none absolute -right-12 -top-12 h-32 w-32 rounded-full opacity-0 blur-3xl transition-opacity group-hover:opacity-50"
                style={{ background: BURNT }}
              />
              <div className="mb-3 flex items-center justify-between">
                <div
                  className="flex h-9 w-9 items-center justify-center rounded-xl"
                  style={{ background: sev.bg, border: `1px solid ${sev.color}55`, color: sev.color }}
                >
                  <Icon size={15} />
                </div>
                <span
                  className="rounded-full px-2.5 py-0.5 text-[10.5px] font-bold uppercase tracking-wider"
                  style={{ background: sev.bg, color: sev.color }}
                >
                  {sev.label}
                </span>
              </div>
              <h3 className="text-[14.5px] font-bold leading-snug text-white">{ins.title}</h3>
              <div
                className="mt-1.5 flex items-center gap-1 text-[11.5px] font-bold"
                style={{ color: positive ? GREEN : RED }}
              >
                {positive ? <TrendingUp size={11} /> : <TrendingDown size={11} />}
                Estimated impact: {ins.impact}
              </div>
              <p className="mt-3 flex-1 text-[12.5px] leading-relaxed text-white/65">
                {ins.explanation.split(/(\*\*[^*]+\*\*|\*[^*]+\*)/g).map((c, i) => {
                  if (c.startsWith("**") && c.endsWith("**"))
                    return <strong key={i} className="font-bold text-white">{c.slice(2, -2)}</strong>;
                  if (c.startsWith("*") && c.endsWith("*"))
                    return <em key={i} className="italic text-white/85">{c.slice(1, -1)}</em>;
                  return <span key={i}>{c}</span>;
                })}
              </p>
              <button
                onClick={() =>
                  onTriggerAria(ins.prompt, {
                    workflow: ins.workflow,
                    campaign: ins.campaign,
                    adGroup: ins.adGroup,
                    impact: ins.impact,
                    insightId: ins.id,
                    insightTitle: ins.title,
                  })
                }
                className="mt-4 flex w-full items-center justify-center gap-1.5 rounded-lg px-4 py-2 text-[12.5px] font-bold text-white transition-transform hover:scale-[1.02] active:scale-[0.98]"
                style={{
                  background: `linear-gradient(135deg, ${BURNT}, #b8390a)`,
                  boxShadow: `0 8px 24px ${BURNT}55`,
                }}
              >
                <ActionIcon size={13} />
                {ins.actionLabel}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default InsightsTab;
